import React from "react";
import { motion } from "framer-motion";
import { ArrowRight, Play } from "lucide-react";
import { IMAGES } from "@/lib/data";

const HERO_IMG = IMAGES.tsunamiBg;

export const Hero = () => {
  return (
    <section
      id="top"
      data-testid="hero-section"
      className="relative min-h-screen w-full overflow-hidden"
    >
      {/* Background image */}
      <div className="absolute inset-0">
        <img
          src={HERO_IMG}
          alt="Cinematic tsunami abstract horizon"
          className="w-full h-full object-cover object-center scale-110"
          style={{ filter: "contrast(1.05) saturate(0.85) hue-rotate(-12deg)" }}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-ink-950 via-ink-950/75 to-ink-950/40" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_30%_70%,rgba(212,175,55,0.18),transparent_55%)]" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_80%_20%,rgba(140,30,30,0.18),transparent_55%)]" />
      </div>

      {/* Faint top-right accent label */}
      <motion.div
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 1.2, duration: 0.8 }}
        className="absolute top-28 right-8 md:right-16 z-10 text-right max-w-[260px]"
      >
        <div className="text-[10px] tracking-[0.4em] text-gold uppercase mb-2">
          Album · 2026
        </div>
        <div className="font-serif italic text-xl md:text-2xl text-slate-200">
          "He doesn't ride the storm — he <span className="text-shimmer-gold not-italic">becomes it.</span>"
        </div>
      </motion.div>

      {/* Main content — asymmetric bottom-left anchor */}
      <div className="relative z-10 min-h-screen flex flex-col justify-end pb-24 md:pb-32 px-6 md:px-16">
        <motion.div
          initial="hidden"
          animate="show"
          variants={{
            hidden: {},
            show: { transition: { staggerChildren: 0.12, delayChildren: 0.25 } },
          }}
          className="max-w-[1200px]"
        >
          <motion.div
            variants={{ hidden: { opacity: 0, y: 20 }, show: { opacity: 1, y: 0 } }}
            className="flex items-center gap-4 mb-6"
          >
            <span className="w-12 h-px bg-gold" />
            <span className="text-xs tracking-[0.4em] uppercase text-gold">
              An American Phenomenon
            </span>
          </motion.div>

          <motion.h1
            variants={{ hidden: { opacity: 0, y: 40 }, show: { opacity: 1, y: 0 } }}
            data-testid="hero-title"
            className="font-display font-black uppercase leading-[0.85] tracking-tighter"
          >
            <span className="block text-6xl sm:text-7xl md:text-8xl lg:text-[10rem]">
              CASSIUS
            </span>
            <span className="block text-6xl sm:text-7xl md:text-8xl lg:text-[10rem] text-shimmer-gold">
              GRATE
            </span>
          </motion.h1>

          <motion.div
            variants={{ hidden: { opacity: 0, y: 30 }, show: { opacity: 1, y: 0 } }}
            className="mt-8 flex items-baseline gap-3 flex-wrap"
          >
            <span className="font-serif italic text-2xl md:text-4xl text-slate-200">
              Witness the
            </span>
            <span className="font-display font-black text-3xl md:text-5xl text-gold glow-magenta tracking-tight">
              TSUNAMI.
            </span>
          </motion.div>

          <motion.p
            variants={{ hidden: { opacity: 0, y: 20 }, show: { opacity: 1, y: 0 } }}
            className="mt-6 max-w-xl text-base md:text-lg text-slate-300 font-light leading-relaxed"
          >
            Hip-hop talk. R&B silk. Pop voltage. A new American tsunami that doesn't ask
            permission — it just breaks on shore.
          </motion.p>

          <motion.div
            variants={{ hidden: { opacity: 0, y: 20 }, show: { opacity: 1, y: 0 } }}
            className="mt-10 flex flex-wrap items-center gap-4"
          >
            <a
              href="#merch"
              data-testid="hero-cta-shop"
              className="group inline-flex items-center gap-3 bg-gold hover:bg-gold-light text-ink-950 font-black uppercase tracking-[0.2em] text-sm px-8 py-4 rounded-sm shadow-[0_0_30px_rgba(212,175,55,0.35)] hover:shadow-[0_0_50px_rgba(253,224,71,0.6)] transition-all"
            >
              Shop the Tsunami
              <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
            </a>
            <a
              href="#tour"
              data-testid="hero-cta-tour"
              className="inline-flex items-center gap-3 border border-gold/50 text-gold hover:bg-gold/10 hover:border-gold-light font-bold uppercase tracking-[0.2em] text-sm px-8 py-4 rounded-sm transition-all"
            >
              Tour Tickets
            </a>
            <a
              href="#music"
              data-testid="hero-cta-listen"
              className="inline-flex items-center gap-3 text-slate-200 hover:text-white text-sm uppercase tracking-[0.2em] font-medium group"
            >
              <span className="w-10 h-10 rounded-full border border-white/20 flex items-center justify-center group-hover:border-magenta-hot group-hover:text-magenta-hot transition-colors">
                <Play size={14} fill="currentColor" />
              </span>
              Stream the Album
            </a>
          </motion.div>
        </motion.div>
      </div>

      {/* Scroll cue */}
      <div className="absolute bottom-6 left-1/2 -translate-x-1/2 z-10 text-[10px] tracking-[0.4em] text-slate-500 animate-tide-float">
        SCROLL TO DROWN
      </div>
    </section>
  );
};
